/**
 * ISO 15765-2 reassembly.
 *
 * A CAN frame carries eight bytes. Anything longer — a VIN, a module's data by
 * identifier, most of what a manufacturer-specific read returns — arrives split
 * across frames with a protocol control byte on each. Until now this codebase
 * read only what fitted in one frame, which is why the Ford probe could ask for
 * an odometer and quietly truncate the answer.
 *
 * An ELM327 presents the same conversation in one of three shapes depending on
 * how it is configured, and adapters disagree about the default. Rather than
 * pin the adapter to one mode and hope, this parses all three:
 *
 *   Raw frames, headers on          7E8 10 14 62 F1 90 31 32 33
 *                                   7E8 21 34 35 36 37 38 39 41
 *
 *   Raw frames, headers off         10 14 62 F1 90 31 32 33
 *                                   21 34 35 36 37 38 39 41
 *
 *   Adapter-assembled               014
 *                                   0: 62 F1 90 31 32 33
 *                                   1: 34 35 36 37 38 39 41
 *
 * The third is what an ELM327 prints with auto-formatting on: a total length in
 * hex, then indexed lines with the control bytes already stripped.
 */

export interface IsoTpMessage {
  /** The responding address, when the adapter was printing headers. */
  header: string | null;
  data: number[];
  /**
   * The length the first frame declared, when there was one.
   *
   * Kept so a caller can tell a complete message from one cut short: a reply
   * that stops early is a bad read, and reporting it as good is how a truncated
   * odometer becomes a number somebody believes.
   */
  declared: number | null;
  complete: boolean;
}

const HEX_LINE = /^[0-9A-F\s:]+$/;

function bytesOf(text: string): number[] {
  const out: number[] = [];
  for (const pair of text.trim().split(/\s+/)) {
    if (/^[0-9A-F]{2}$/.test(pair)) out.push(parseInt(pair, 16));
    // Some adapters run the payload together rather than spacing it.
    else if (/^[0-9A-F]+$/.test(pair) && pair.length % 2 === 0) {
      for (let i = 0; i < pair.length; i += 2) out.push(parseInt(pair.slice(i, i + 2), 16));
    }
  }
  return out;
}

/** Lines that are adapter chatter rather than a reply. */
function isNoise(line: string): boolean {
  const t = line.trim().toUpperCase();
  if (!t || t === '>' || t === 'OK') return true;
  if (/^(SEARCHING|BUS INIT|STOPPED|NO DATA|CAN ERROR|UNABLE TO CONNECT|\?)/.test(t)) return true;
  return !HEX_LINE.test(t);
}

/**
 * Reassemble one adapter response into whole messages.
 *
 * More than one module can answer the same request, so this returns a list.
 * Frames are grouped by responding address; without headers there is no way to
 * tell two responders apart and they are treated as one conversation, which is
 * the best available reading of an ambiguous transcript.
 */
export function parseIsoTp(response: string): IsoTpMessage[] {
  const lines = response.split(/[\r\n]+/).filter((l) => !isNoise(l));
  if (lines.length === 0) return [];

  // The adapter-assembled shape: a bare length, then indexed lines.
  const indexed = lines.filter((l) => /^\s*[0-9A-F]\s*:/.test(l));
  if (indexed.length > 0) {
    const lengthLine = lines.find((l) => /^\s*[0-9A-F]{3}\s*$/.test(l));
    const declared = lengthLine ? parseInt(lengthLine.trim(), 16) : null;
    const data: number[] = [];
    // Sorted by their own index, since a line can arrive out of order.
    for (const line of [...indexed].sort((a, b) => parseInt(a.trim(), 16) - parseInt(b.trim(), 16))) {
      data.push(...bytesOf(line.slice(line.indexOf(':') + 1)));
    }
    const trimmed = declared === null ? data : data.slice(0, declared);
    return [{ header: null, data: trimmed, declared, complete: declared === null || data.length >= declared }];
  }

  // Raw frames. Group by header when there is one.
  const groups = new Map<string, number[][]>();
  for (const line of lines) {
    const bytes = bytesOf(line);
    if (bytes.length === 0) continue;
    // An 11-bit header prints as three hex digits, so the line starts with an
    // odd-length token. 29-bit prints as eight.
    const first = line.trim().split(/\s+/)[0];
    const hasHeader = /^[0-9A-F]{3}$|^[0-9A-F]{8}$/.test(first);
    const header = hasHeader ? first : '';
    const frame = hasHeader ? bytesOf(line.slice(line.indexOf(first) + first.length)) : bytes;
    if (frame.length === 0) continue;
    const bucket = groups.get(header) ?? [];
    bucket.push(frame);
    groups.set(header, bucket);
  }

  const out: IsoTpMessage[] = [];
  for (const [header, frames] of groups) {
    out.push(assemble(header || null, frames));
  }
  return out;
}

function assemble(header: string | null, frames: number[][]): IsoTpMessage {
  const first = frames[0];
  const type = first[0] >> 4;

  // Single frame: low nibble is the length.
  if (type === 0x0) {
    const len = first[0] & 0x0f;
    const data = first.slice(1, 1 + len);
    return { header, data, declared: len, complete: data.length === len };
  }

  // First frame: twelve-bit length across the first two bytes, then
  // consecutive frames each carrying six payload bytes after their index.
  if (type === 0x1) {
    const declared = ((first[0] & 0x0f) << 8) | first[1];
    const data = first.slice(2);
    for (const frame of frames.slice(1)) {
      if (frame[0] >> 4 !== 0x2) continue; // flow control and stray frames
      data.push(...frame.slice(1));
    }
    return { header, data: data.slice(0, declared), declared, complete: data.length >= declared };
  }

  // Not framed at all: some adapters strip the control byte themselves on a
  // short reply. Taking it whole is better than discarding a real answer.
  const data = frames.flat();
  return { header, data, declared: null, complete: true };
}

/**
 * Commands that make an ELM327 answer a multi-frame request at all.
 *
 * Without a flow control frame the vehicle sends its first frame and stops. The
 * adapter can send one itself, but only when told to: ATFCSM1 selects the
 * user-supplied frame, ATFCSH sets who it comes from and ATFCSD its content —
 * block size zero and separation time zero, meaning "send it all, as fast as
 * you like".
 *
 * `tester` is the address this tool transmits from, which is what the vehicle
 * expects the flow control to arrive on.
 */
export function flowControlCommands(tester: string): string[] {
  return [`ATFCSH${tester}`, 'ATFCSD300000', 'ATFCSM1'];
}

/** The first message whose payload answers the given service and identifier. */
export function findResponse(messages: IsoTpMessage[], service: number, did?: number): IsoTpMessage | null {
  for (const m of messages) {
    if (m.data[0] !== service + 0x40) continue;
    if (did === undefined) return m;
    if (((m.data[1] << 8) | m.data[2]) === did) return m;
  }
  return null;
}
